import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DealForm from '../deals/DealForm';
import useDealFormCalculations from '../../hooks/useDealFormCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Inline "Create New Deal" sub-dialog used by the Refinance Wizard so the
 * operator can spin up the new deal without leaving the refi flow.
 *
 * Reuses the existing DealForm component verbatim (no field duplication).
 *
 *   open                — controls dialog visibility
 *   onOpenChange(bool)  — close handler
 *   getAuthHeaders      — auth header provider (passed through to DealForm)
 *   prefillMerchantId   — merchant contact id from the OLD deal; pre-selects
 *                         that merchant on the new-deal form so the operator
 *                         doesn't re-pick it. Override allowed.
 *   onCreated(deal)     — fired when POST /api/deals succeeds. The wizard uses
 *                         this to auto-select the returned deal as the refi
 *                         target (zero extra clicks).
 */
const initialFormData = {
  deal_id: '',
  deal_shortcode: '',
  broker_id: '',
  company_id: '',
  funder_id: '',
  funder_percentage: '100',
  deal_name: '',
  funding_date: new Date().toISOString().split('T')[0],
  funding_amount: '',
  fees: '0',
  refi_balance_payoff: '0',
  deposit_amount: '',
  rate: '',
  term_days: '',
  payback: '',
  payment_amount: '',
  estimated_end_date: '',
  weekly: false,
  status: 'Funded',
  broker_commission: '0',
  commission_rate: '',
  is_iso_originated: false,
  refi: false,
  refi_from_deal: '',
  notes: '',
};

const InlineNewDealDialog = ({
  open,
  onOpenChange,
  getAuthHeaders,
  prefillMerchantId,
  prefillSourceDeal,
  onCreated,
}) => {
  const [formData, setFormData] = useState({ ...initialFormData });
  const [brokers, setBrokers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [funders, setFunders] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reuse the same payload preparer used by /pages/Deals.js — guarantees the
  // inline-created deal lands in the DB with the EXACT shape the standard
  // create flow produces (balance derived from payback, types coerced, etc.).
  // Single source of truth for new-deal payload shape.
  const { handleFieldChange, preparePayload } = useDealFormCalculations(merchants, setFormData);

  // Auto-detect ISO deal: when the chosen broker is ISO-tagged, default the +2% charge on (override allowed).
  useEffect(() => {
    if (!formData.broker_id) return;
    const b = (brokers || []).find(x => x.id === formData.broker_id);
    if (b && (b.contact_types || []).includes('ISO')) {
      setFormData(prev => ({ ...prev, is_iso_originated: true }));
    }
  }, [formData.broker_id, brokers]);

  // Load reference data once when the dialog opens.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError('');
    setFormData({
      ...initialFormData,
      // Pre-select merchant from the old deal — operator can override.
      company_id: prefillMerchantId || prefillSourceDeal?.company_id || '',
      // Refi prefills (Josh 07/28): shortcode follows the company, name
      // carries over, and the refinance link to the source deal is obvious.
      deal_shortcode: prefillSourceDeal?.deal_shortcode || '',
      deal_name: prefillSourceDeal?.deal_name || '',
      refi: !!prefillSourceDeal,
      refi_from_deal: prefillSourceDeal?.id || '',
      // 08/05 fix: the backend REQUIRES broker_id + funder_id — a refi target
      // created without them 400s ("Invalid broker_id"), which is why deal
      // creation silently failed. Carry them over from the source deal.
      broker_id: prefillSourceDeal?.broker_id || '',
      funder_id: prefillSourceDeal?.funder_id || '',
      commission_rate: prefillSourceDeal?.commission_rate ?? '',
      commission_type: prefillSourceDeal?.commission_type || '',
    });

    // 08/31 (Josh, 10795→10942: "it still didnt work") — these two loads are
    // heavy (~3MB) and die when the backend is mid-redeploy, leaving a dead
    // form with no merchant picker. Retry twice with backoff before giving up,
    // and make the give-up LOUD.
    const fetchRefs = () => Promise.all([
      axios.get(`${API}/contacts?limit=1000`, { headers: getAuthHeaders() }),
      axios.get(`${API}/deals?limit=1000`, { headers: getAuthHeaders() }),
    ]);
    const wait = (ms) => new Promise(r => setTimeout(r, ms));
    fetchRefs()
      .catch(() => wait(1500).then(fetchRefs))
      .catch(() => wait(3000).then(fetchRefs))
      .then(([contactsRes, dealsRes]) => {
        if (cancelled) return;
        const contacts = Array.isArray(contactsRes.data) ? contactsRes.data : (contactsRes.data?.contacts || []);
        // Match the same partitioning used on the Deals page.
        const isBroker = (c) => (c.contact_types || []).some(t => ['Broker', 'ISO'].includes(t));
        const isMerchant = (c) => (c.contact_types || []).includes('Merchant');
        const isFunder = (c) => (c.contact_types || []).includes('Funder');
        setBrokers(contacts.filter(isBroker));
        setMerchants(contacts.filter(isMerchant));
        setFunders(contacts.filter(isFunder));
        const dealsList = Array.isArray(dealsRes.data) ? dealsRes.data : (dealsRes.data?.deals || []);
        setAllDeals(dealsList);
        // 09/03 (Josh: "its not saving") — the Deal ID field started BLANK
        // with no auto-suggest, and the blank-deal-id guard was silent, so
        // Create quietly did nothing. Suggest the next numeric deal number.
        const maxNum = dealsList.reduce((m, dd) => {
          const n = parseInt(dd.deal_id, 10);
          return Number.isFinite(n) && String(dd.deal_id).match(/^\d+$/) ? Math.max(m, n) : m;
        }, 0);
        if (maxNum > 0) {
          setFormData(prev => (prev.deal_id?.trim() ? prev : { ...prev, deal_id: String(maxNum + 1) }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load reference data');
          toast.error('Failed to load merchants/deals after 3 tries — close this dialog and reopen it (the server may be restarting)');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, prefillMerchantId, prefillSourceDeal, getAuthHeaders]);

  const handleSubmit = async (e) => {
    // 09/04 ROOT CAUSE of "its not saving" from the refi wizard: DealForm
    // renders <form onSubmit={onSubmit}> and this handler never called
    // preventDefault, so clicking Create triggered the browser's DEFAULT form
    // submission — a full page navigation that killed the dialog, aborted the
    // in-flight POST, and wiped every toast before it rendered. Deals.js's own
    // handler preventDefaults first (line ~297); this one must too.
    e?.preventDefault?.();
    setError('');
    // Minimal client-side guard — server validation is the source of truth.
    if (!formData.deal_id?.trim()) {
      setError('Deal ID is required');
      toast.error('Deal ID is required — enter the next deal number (auto-suggest may still be loading)');
      return;
    }
    if (!formData.company_id) {
      setError('Merchant is required');
      toast.error('Merchant is required — check the contacts tab of the form');
      return;
    }
    // Server rejects deals without these (400 Invalid broker_id / funder_id) —
    // guard client-side so the operator sees WHY before submitting. Toast too:
    // the inline error banner sits above the fold in a scrolled dialog.
    if (!formData.broker_id) {
      setError('ISO / Broker is required (the backend rejects deals without one)');
      toast.error('ISO / Broker is required — select one before creating the deal');
      return;
    }
    if (!formData.funder_id) {
      setError('Funder is required (the backend rejects deals without one)');
      toast.error('Funder is required — select one before creating the deal');
      return;
    }
    if (!parseFloat(formData.funding_amount)) {
      setError('Funding amount is required');
      toast.error('Funding amount is required');
      return;
    }

    // Build payload via the canonical preparer used by the Deals page — same
    // type coercion, same `balance = payback` derivation, same null-stripping.
    const payload = preparePayload(formData, false);
    if (!payload.refi) {
      delete payload.refi_from_deal;
      delete payload.refi_balance_payoff;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/deals`, payload, { headers: getAuthHeaders() });
      const created = res.data;
      toast.success(`Deal #${created.deal_id} created`);
      onCreated?.(created);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Failed to create deal';
      if (Array.isArray(detail)) {
        msg = detail.map(e => `${e.loc?.[e.loc.length - 1] || 'field'}: ${e.msg}`).join(', ');
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="inline-new-deal-dialog"
      >
        <DialogHeader>
          <DialogTitle>Create New Deal</DialogTitle>
          <DialogDescription>
            The new deal will be auto-selected as the refinance target after creation.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : (
          <DealForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={submitting ? () => {} : handleSubmit}
            onCancel={() => onOpenChange(false)}
            error={error}
            editingDeal={null}
            brokers={brokers}
            merchants={merchants}
            funders={funders}
            allDeals={allDeals}
            handleFieldChange={handleFieldChange}
            getAuthHeaders={getAuthHeaders}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InlineNewDealDialog;
